import type { ComponentProps, ReactNode } from 'react'

import { SortDescendingIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Table. Figma: 10 / Components / Table and 24 / Blocks / Tables.
 * Container: 12px corners, Colors/surface/raised. Header and footer rows sit on Colors/surface/muted.
 * TableHead: 40px, Roboto / text-sm / medium, optional sort icon (Colors/icon/brand when active).
 * TableCell: 48px rows, Roboto / text-base values, 14px supporting text, Colors/border/subtle dividers.
 */
function TableContainer({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='table-container'
            className={cn('w-full overflow-hidden rounded-xl bg-surface-raised', className)}
            {...props}
        />
    )
}

function Table({ className, ...props }: ComponentProps<'table'>) {
    return (
        <div data-slot='table-scroll' className='relative w-full overflow-x-auto'>
            <table
                data-slot='table'
                className={cn('w-full caption-bottom border-collapse text-base text-text-primary', className)}
                {...props}
            />
        </div>
    )
}

function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
    return (
        <thead
            data-slot='table-header'
            className={cn('bg-surface-muted', className)}
            {...props}
        />
    )
}

function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
    return (
        <tbody
            data-slot='table-body'
            className={cn('[&_tr:last-child]:border-0', className)}
            {...props}
        />
    )
}

interface TableRowProps extends ComponentProps<'tr'> {
    selected?: boolean
}

function TableRow({ selected = false, className, ...props }: TableRowProps) {
    return (
        <tr
            data-slot='table-row'
            data-selected={selected ? 'true' : undefined}
            aria-selected={selected || undefined}
            className={cn('border-b border-border-subtle transition-colors [tbody_&]:hover:bg-surface-subtle data-[selected=true]:bg-text-selection-background data-[selected=true]:hover:bg-text-selection-background', className)}
            {...props}
        />
    )
}

type SortDirection = 'ascending' | 'descending' | 'none'

interface TableHeadProps extends ComponentProps<'th'> {
    align?: 'start' | 'end' | 'center'
    sort?: SortDirection | 'unsorted'
    onSort?: () => void
}

function TableHead({ align = 'start', sort, onSort, className, children, ...props }: TableHeadProps) {
    const sortable = sort !== undefined
    const active = sort === 'ascending' || sort === 'descending'
    const content = (
        <span className={cn('inline-flex items-center gap-1', align === 'end' && 'flex-row-reverse')}>
            {children}
            {sortable && (
                <SortDescendingIcon
                    aria-hidden='true'
                    className={cn('size-4 shrink-0', active ? 'text-icon-brand' : 'text-icon-secondary', sort === 'ascending' && 'rotate-180')}
                />
            )}
        </span>
    )

    return (
        <th
            data-slot='table-head'
            aria-sort={active ? sort : sortable ? 'none' : undefined}
            className={cn(
                'h-10 px-4 text-sm font-medium whitespace-nowrap text-text-secondary',
                align === 'start' && 'text-left',
                align === 'end' && 'text-right',
                align === 'center' && 'text-center',
                className
            )}
            {...props}
        >
            {onSort
                ? (
                    <button type='button' onClick={onSort} className='cursor-pointer rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus'>
                        {content}
                    </button>
                )
                : content}
        </th>
    )
}

interface TableCellProps extends ComponentProps<'td'> {
    align?: 'start' | 'end' | 'center'
}

function TableCell({ align = 'start', className, ...props }: TableCellProps) {
    return (
        <td
            data-slot='table-cell'
            className={cn(
                'h-12 px-4 py-3 align-middle',
                align === 'end' && 'text-right',
                align === 'center' && 'text-center',
                className
            )}
            {...props}
        />
    )
}

interface TableCellTextProps extends ComponentProps<'div'> {
    primary: ReactNode
    supporting?: ReactNode
    strong?: boolean
    icon?: ReactNode
}

/* Cell text with optional supporting text (text-detail and text-detail-strong), with an optional leading icon. */
function TableCellText({ primary, supporting, strong = false, icon, className, ...props }: TableCellTextProps) {
    return (
        <div data-slot='table-cell-text' className={cn('flex items-center gap-3', className)} {...props}>
            {icon && (
                <span className='inline-flex shrink-0 text-icon-secondary [&_svg]:size-5'>
                    {icon}
                </span>
            )}
            <div className='flex min-w-0 flex-col'>
                <span className={cn('truncate text-base text-text-primary', strong && 'font-medium')}>
                    {primary}
                </span>
                {supporting && (
                    <span className='truncate text-sm text-text-secondary'>
                        {supporting}
                    </span>
                )}
            </div>
        </div>
    )
}

/* Footer bar under a table: result count on the left, pagination or a See all action on the right. */
function TableFooterBar({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='table-footer-bar'
            className={cn('flex flex-wrap items-center justify-between gap-3 bg-surface-muted px-4 py-2.5 text-sm text-text-secondary', className)}
            {...props}
        />
    )
}

/* Toolbar row above a table, on the header surface (search, add action). */
function TableToolbar({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='table-toolbar'
            className={cn('flex flex-wrap items-center gap-3 border-b border-border-subtle px-4 py-3', className)}
            {...props}
        />
    )
}

function TableCaption({ className, ...props }: ComponentProps<'caption'>) {
    return (
        <caption
            data-slot='table-caption'
            className={cn('py-3 text-sm text-text-secondary', className)}
            {...props}
        />
    )
}

export {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableCellText,
    TableContainer,
    TableFooterBar,
    TableHead,
    TableHeader,
    TableRow,
    TableToolbar
}
export type { SortDirection }
